// CockpitProPreview - Aperçu du Cockpit Pro pour les particuliers
// Montre ce qu'ils pourraient avoir s'ils passent Pro
// Avec invitation à s'inscrire en tant que Pro

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, ArrowRight, Wand2, Briefcase, Users, Bot, 
  ShoppingBag, CreditCard, BarChart3, Calendar, Mail,
  MessageCircle, FileText, Shield, Crown, Check, X,
  Building2, Phone, Globe
} from 'lucide-react';
import { useCurrentUser } from '../../hooks/useCurrentUser';

// Couleurs
const C = {
  navy: '#1E3A5F',
  orange: '#F97316',
  green: '#22C55E',
};

// Fonctionnalités du Cockpit Pro
const PRO_FEATURES = [
  { icon: Bot, title: 'Super Assistant IA', desc: 'Formé à ton métier, il gère tes tâches' },
  { icon: Users, title: 'CRM complet', desc: 'Prospects, clients, devis automatiques' },
  { icon: ShoppingBag, title: 'Boutique en ligne', desc: 'Vends tes produits et services' },
  { icon: CreditCard, title: 'Encaissement', desc: 'Paiement sécurisé, terminal NFC' },
  { icon: Calendar, title: 'Agenda Pro', desc: 'RDV clients, visios intégrées' },
  { icon: Mail, title: 'Messagerie unifiée', desc: 'Tous tes mails pro au même endroit' },
  { icon: BarChart3, title: 'Tableau de bord', desc: 'Stats, CA, performance' },
  { icon: Globe, title: 'Site internet', desc: 'Ton site dédié (Business & Premium)' },
];

export default function CockpitProPreview() {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useCurrentUser();
  const [showProSignupModal, setShowProSignupModal] = useState(false);

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: `linear-gradient(135deg, ${C.navy} 0%, #0F2744 100%)`,
    }}>
      
      {/* Header */}
      <header style={{
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
      }}>
        {/* Retour */}
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 16px',
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 10,
            color: '#fff',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={18} />
          Retour
        </button>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: `linear-gradient(135deg, ${C.orange} 0%, #EA580C 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Briefcase size={22} color="#fff" />
          </div>
          <span style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 800,
            fontSize: 20,
            color: '#fff',
          }}>
            Cockpit <span style={{ color: C.orange }}>Pro</span>
          </span>
        </div>

        {/* CTA */}
        <button
          onClick={() => setShowProSignupModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 20px',
            background: `linear-gradient(135deg, ${C.green} 0%, #16A34A 100%)`,
            border: 'none',
            borderRadius: 10,
            color: '#fff',
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)',
          }}
          data-testid="open-pro-signup-btn"
        >
          <Crown size={18} />
          Devenir Pro
        </button>
      </header>

      {/* Contenu */}
      <main style={{ padding: '60px 24px', maxWidth: 1100, margin: '0 auto' }}>
        
        {/* Titre */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: 'center', marginBottom: 60 }}
        >
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 20px',
            background: 'rgba(34, 197, 94, 0.15)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: 50,
            marginBottom: 24,
          }}>
            <Briefcase size={18} color={C.green} />
            <span style={{ color: C.green, fontWeight: 700, fontSize: 14 }}>
              Espace Professionnel
            </span>
          </div>
          
          <h1 style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 'clamp(32px, 5vw, 48px)',
            fontWeight: 800,
            color: '#fff',
            lineHeight: 1.1,
            marginBottom: 20,
          }}>
            Le Cockpit des <span style={{ color: C.orange }}>professionnels</span>
          </h1>
          
          <p style={{
            fontSize: 18,
            color: 'rgba(255,255,255,0.7)',
            maxWidth: 600,
            margin: '0 auto',
            lineHeight: 1.6,
          }}>
            Gère ton activité pro avec un assistant IA formé à ton métier, 
            un CRM complet, une boutique en ligne et bien plus.
          </p>
        </motion.div>

        {/* Grille des fonctionnalités */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 20,
            marginBottom: 60,
          }}
        >
          {PRO_FEATURES.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div
                key={i}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 16,
                  padding: 24,
                }}
              >
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: `${C.orange}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 16,
                }}>
                  <Icon size={24} color={C.orange} />
                </div>
                <h3 style={{ 
                  color: '#fff', 
                  fontSize: 17, 
                  fontWeight: 700, 
                  margin: 0, 
                  marginBottom: 8 
                }}>
                  {feature.title}
                </h3>
                <p style={{ 
                  color: 'rgba(255,255,255,0.6)', 
                  fontSize: 14, 
                  margin: 0,
                  lineHeight: 1.5,
                }}>
                  {feature.desc}
                </p>
              </div>
            );
          })}
        </motion.div>

        {/* CTA Final */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          style={{
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: 24,
            padding: 40,
            textAlign: 'center',
          }}
        >
          <Crown size={48} color={C.green} style={{ marginBottom: 20 }} />
          
          <h2 style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 28,
            fontWeight: 800,
            color: '#fff',
            margin: 0,
            marginBottom: 16,
          }}>
            Prêt à passer Pro ?
          </h2>
          
          <p style={{
            color: 'rgba(255,255,255,0.7)',
            fontSize: 16,
            marginBottom: 28,
            maxWidth: 500,
            margin: '0 auto 28px',
          }}>
            Loue ta boutique dans le centre commercial virtuel et profite de tous les outils 
            pour développer ton activité.
          </p>

          <button
            onClick={() => setShowProSignupModal(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '18px 36px',
              background: `linear-gradient(135deg, ${C.green} 0%, #16A34A 100%)`,
              border: 'none',
              borderRadius: 14,
              color: '#fff',
              fontWeight: 700,
              fontSize: 17,
              cursor: 'pointer',
              boxShadow: '0 8px 30px rgba(34, 197, 94, 0.4)',
            }}
            data-testid="final-pro-signup-btn"
          >
            <Crown size={20} />
            Ouvrir mon compte Pro
            <ArrowRight size={18} />
          </button>
          
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginTop: 16 }}>
            Offre Fondateur : -50% sur le pas de porte et le loyer à vie
          </p>
        </motion.div>
      </main>

      {/* Modal inscription Pro */}
      <ProSignupModal 
        isOpen={showProSignupModal} 
        onClose={() => setShowProSignupModal(false)} 
      />
    </div>
  );
}

// Modal d'inscription Pro
function ProSignupModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 20,
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#fff',
            borderRadius: 24,
            width: '100%',
            maxWidth: 500,
            padding: 32,
            position: 'relative',
          }}
        >
          {/* Bouton fermer */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: 'none',
              background: '#F1F5F9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={18} color="#64748B" />
          </button>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: 18,
              background: `linear-gradient(135deg, ${C.green} 0%, #16A34A 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Briefcase size={32} color="#fff" />
            </div>
            <h2 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 24,
              fontWeight: 700,
              color: C.navy,
              margin: 0,
              marginBottom: 8,
            }}>
              Ouvre ton compte Pro
            </h2>
            <p style={{ color: '#64748B', fontSize: 14, margin: 0 }}>
              Loue ta boutique dans le centre commercial virtuel
            </p>
          </div>

          {/* Avantages */}
          <div style={{ marginBottom: 28 }}>
            {[
              'Super Assistant IA formé à ton métier',
              'CRM complet (prospects, clients, devis)',
              'Boutique en ligne sans commission',
              'Encaissement sécurisé',
            ].map((item, i) => (
              <div key={i} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 10, 
                marginBottom: 10,
              }}>
                <Check size={18} color={C.green} />
                <span style={{ fontSize: 14, color: '#475569' }}>{item}</span>
              </div>
            ))}
          </div>

          {/* CTA vers page d'inscription Pro */}
          <button
            onClick={() => {
              onClose();
              navigate('/pro/fondateur');
            }}
            style={{
              width: '100%',
              padding: '16px',
              background: `linear-gradient(135deg, ${C.green} 0%, #16A34A 100%)`,
              border: 'none',
              borderRadius: 12,
              color: '#fff',
              fontWeight: 700,
              fontSize: 16,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)',
              marginBottom: 16,
            }}
            data-testid="go-to-fondateur-btn"
          >
            <Crown size={20} />
            Découvrir l'offre Fondateur
            <ArrowRight size={18} />
          </button>

          <p style={{ textAlign: 'center', color: '#64748B', fontSize: 13 }}>
            Offre limitée aux 100 premiers • -50% à vie
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
